import { lazy, Suspense, useEffect, useState } from 'react'
import '@uiw/react-md-editor/markdown-editor.css'
import '@uiw/react-markdown-preview/markdown.css'

const MDEditor = lazy(() => import('@uiw/react-md-editor'))

function useResolvedColorMode(): 'light' | 'dark' {
  const [mode, setMode] = useState<'light' | 'dark'>(() =>
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
      ? 'dark'
      : 'light'
  )
  useEffect(() => {
    const root = document.documentElement
    const update = () => setMode(root.classList.contains('dark') ? 'dark' : 'light')
    update()
    const observer = new MutationObserver(update)
    observer.observe(root, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [])
  return mode
}

interface Props {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  height?: number
  preview?: 'live' | 'edit' | 'preview'
  /** Disable when a parent dialog/sheet already owns document scroll locking. */
  overflow?: boolean
}

export function MarkdownField({
  value,
  onChange,
  placeholder,
  height = 240,
  preview = 'live',
  overflow = true,
}: Props) {
  const colorMode = useResolvedColorMode()
  return (
    <Suspense
      fallback={<div className="h-60 w-full animate-pulse rounded-md border bg-muted" />}
    >
      <div data-color-mode={colorMode}>
        <MDEditor
          value={value}
          onChange={(v) => onChange(v ?? '')}
          preview={preview}
          height={height}
          overflow={overflow}
          textareaProps={{ placeholder }}
        />
      </div>
    </Suspense>
  )
}

export function MarkdownPreview({ source, transparent = false }: { source: string; transparent?: boolean }) {
  const colorMode = useResolvedColorMode()
  return (
    <Suspense fallback={null}>
      <div data-color-mode={colorMode}>
        <MDEditorPreview
          source={source}
          style={transparent ? { backgroundColor: 'transparent', color: 'inherit' } : undefined}
        />
      </div>
    </Suspense>
  )
}

// Render-only markdown preview used for comments etc.
const MDEditorPreview = lazy(async () => {
  const mod = await import('@uiw/react-md-editor')
  return { default: mod.default.Markdown as unknown as React.ComponentType<{ source?: string; style?: React.CSSProperties }> }
})
